import { Extension } from "@tiptap/core";
import { Plugin } from "prosemirror-state";

/**
 * After list-item drag-and-drop, ProseMirror may split a single <ol> into
 * multiple sibling lists (e.g. dropping above item #1 inserts a new <ol>
 * before the original). Merge consecutive ordered lists and reset `start`
 * so impression numbering stays 1..N.
 */
export function normalizeOrderedLists(tr) {
  const olType = tr.doc.type.schema.nodes.orderedList;
  if (!olType) return false;

  let changed = false;

  // Repeat until stable — merging can expose new consecutive pairs.
  let safety = 0;
  while (safety < 20) {
    safety += 1;
    const mergeRanges = [];

    tr.doc.descendants((node, pos) => {
      if (!node.isBlock || !node.childCount) return;

      let childPos = pos + 1;
      let i = 0;

      while (i < node.childCount) {
        const child = node.child(i);
        if (child.type !== olType) {
          childPos += child.nodeSize;
          i += 1;
          continue;
        }

        let j = i + 1;
        let rangeEnd = childPos + child.nodeSize;
        while (j < node.childCount && node.child(j).type === olType) {
          rangeEnd += node.child(j).nodeSize;
          j += 1;
        }

        if (j > i + 1) {
          mergeRanges.push({
            from: childPos,
            to: rangeEnd,
            startIndex: i,
            endIndex: j,
            // Resolve parent from a position inside the first list child,
            // not via nodeAt(pos) — nodeAt(0) is the first doc child, not doc.
            parentPos: childPos,
          });
        }

        childPos = rangeEnd;
        i = j;
      }
    });

    if (mergeRanges.length === 0) break;

    // Deepest / later ranges first so earlier positions stay valid.
    mergeRanges.sort((a, b) => b.from - a.from);

    for (const { from, to, startIndex, endIndex, parentPos } of mergeRanges) {
      const $pos = tr.doc.resolve(parentPos);
      const parent = $pos.parent;
      if (!parent || startIndex >= parent.childCount) continue;

      const items = [];
      const firstOl = parent.child(startIndex);
      if (firstOl.type !== olType) continue;

      const attrs = { ...firstOl.attrs, start: 1 };
      for (let k = startIndex; k < endIndex && k < parent.childCount; k += 1) {
        const ol = parent.child(k);
        if (ol.type !== olType) break;
        ol.forEach((item) => items.push(item));
      }

      if (items.length === 0) continue;
      tr.replaceWith(from, to, olType.create(attrs, items));
      changed = true;
    }
  }

  tr.doc.descendants((node, pos) => {
    if (node.type !== olType) return;
    const start = node.attrs.start;
    if (start != null && start !== 1) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, start: 1 });
      changed = true;
    }
  });

  return changed;
}

export const NormalizeOrderedLists = Extension.create({
  name: "normalizeOrderedLists",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged)) return null;

          const tr = newState.tr;
          return normalizeOrderedLists(tr) ? tr : null;
        },
      }),
    ];
  },
});
